import { TFunction } from 'i18next'
import { Cell, TableData, TitledTableData } from 'utils/data'
import { TRANSLATION_KEYS as baseKeys } from './translation-keys'
import { ITranslationKeys } from './ITranslationKeys'
import { translateWeekFormat } from 'utils/payroll/translateWeekFormat'

interface ITranslate {
    t: TFunction<'translation', undefined>
    translation: object
}
interface ITranslateItem extends ITranslate {
    item: string
}
interface ITranslateTableData extends ITranslate {
    table: TableData
}
interface ITranslateTitledTableData extends ITranslate {
    data: TitledTableData
}
type StringKeysInObject<T> = {
    [K in keyof T]: T[K] extends string ? K : never
}[keyof T]

export const translateItem = ({ t, item, translation }: ITranslateItem) => {
    type TranslationStringKeys = StringKeysInObject<ITranslationKeys>
    const tOpts = { nsSeparator: false as const }
    if (item in translation) {
        return t(translation[item as keyof typeof translation], tOpts)
    } else if (item in baseKeys) {
        const key = item as TranslationStringKeys
        return t(baseKeys[key], tOpts)
    }

    return item
}

export const translateTableData = ({
    t,
    table,
    translation,
}: ITranslateTableData) => {
    const translatedHeadings = table.headings?.map((heading: Cell) => {
        const key = heading.value
        const translatedValue = translateItem({ t, item: key, translation })

        return { ...heading, value: translatedValue }
    })

    const translatedRows = table.rows?.map((row: Cell[]) => {
        const [firstCell, ...otherCells] = row
        const key = firstCell.value
        let translatedValue = translateItem({ t, item: key, translation })

        // Apply week format translation (e.g., "Jul Week 1" → "Semana 1 de jul")
        translatedValue = translateWeekFormat(translatedValue)

        return [{ ...firstCell, value: translatedValue }, ...otherCells]
    })

    return {
        ...table,
        headings: translatedHeadings,
        rows: translatedRows,
    }
}
export const translateTitledTableData = ({
    t,
    data,
    translation,
}: ITranslateTitledTableData) => {
    let { title, table } = data
    table = translateTableData({ t, table, translation })
    title = translateItem({ t, item: title, translation })

    return { title, table }
}

export const translateHeadings = (
    keys: string[],
    t: (key: string) => string,
    data: TableData,
): TableData => {
    const toCell = (value: string): Cell => ({ value })
    if (data.headings?.length !== keys.length) {
        throw new Error(
            'Table heading length does not match translation keys length',
        )
    }
    return {
        ...data,
        headings: keys.map(t).map(toCell),
    }
}
